import React from 'react';

// Helpers and small components for the kit editor (CreateKit.js):
// per-hub SKU status, kit checks (warnings only, never block Save),
// and the "apply changes to" scope bar with hub differences.

const PANEL_CATEGORY_ID = 1;
const BOS_CATEGORY_ID = 3;

const normalizeName = (name) => (name || '').toLowerCase().replace(/[^a-z0-9]/g, '');

// Status of one SKU at the hub whose stock is shown. `product` comes from getAllProducts?warehouseId=N.
export const skuStatus = (product, needQty) => {
  if (!product) return 'missing';
  if (!product.active || product.purchasable === false) return 'inactive';
  if (product.allow_out_of_stock || (Number(product.inStock) || 0) >= (needQty || 1)) return 'live';
  return 'nostock';
};

export const SKU_STATUS_STYLE = {
  live: { label: 'Live', background: '#d1e7dd', color: '#0f5132' },
  nostock: { label: 'No stock', background: '#fff3cd', color: '#856404' },
  inactive: { label: 'Inactive', background: '#e9ecef', color: '#6c757d' },
  missing: { label: 'Not at hub', background: '#e9ecef', color: '#6c757d' },
};

export const categoryTitle = (cat, categories, catIndex) => {
  const name = categories.find((c) => c.id === parseInt(cat.category_id))?.name || `Category ${catIndex + 1}`;
  return parseInt(cat.category_id) === BOS_CATEGORY_ID && cat.label ? `${name} · ${cat.label}` : name;
};

// Flags on one SKU row
export const skuFlags = ({ sku, cat, kitName, variantIds }) => {
  const flags = [];
  const isPanel = parseInt(cat.category_id) === PANEL_CATEGORY_ID;
  if (isPanel && /dcr/i.test(kitName || '')) {
    const name = sku.product_name || '';
    if (/non[\s-]?dcr/i.test(name) || /^NSP-/i.test(sku.sku_code || '') || !/dcr/i.test(name)) flags.push('Non-DCR');
  }
  if (isPanel && variantIds && !variantIds.has(sku.product_id)) flags.push('No variant');
  const sameCode = cat.skus.filter((s) => s.sku_code && s.sku_code === sku.sku_code && s.product_id !== sku.product_id);
  const sameName = cat.skus.filter((s) => s.product_id !== sku.product_id && normalizeName(s.product_name) === normalizeName(sku.product_name));
  if (sameCode.length || sameName.length) flags.push('Duplicate');
  return flags;
};

// All checks for the kit. level 'bad' is shown in red but, like 'warn', does not block Save.
export const kitChecks = ({ kitName, kitCategories, categories, panelVariantProducts, minOptional, stockById, stockHubName }) => {
  const checks = [];
  const variantIds = new Set(Object.values(panelVariantProducts || {}).flat());
  kitCategories.forEach((cat, catIndex) => {
    if (!cat.category_id) return;
    const title = categoryTitle(cat, categories, catIndex);
    const isPanel = parseInt(cat.category_id) === PANEL_CATEGORY_ID;
    const byFlag = {};
    cat.skus.forEach((sku) => {
      skuFlags({ sku, cat, kitName, variantIds: isPanel ? variantIds : null }).forEach((f) => {
        (byFlag[f] = byFlag[f] || []).push(sku.sku_code || sku.product_name);
      });
    });
    if (byFlag['Non-DCR']) checks.push({ level: 'bad', catIndex, text: `${title}: ${byFlag['Non-DCR'].length} non-DCR panel(s) in a DCR kit (${byFlag['Non-DCR'].join(', ')})` });
    if (byFlag['No variant']) checks.push({ level: 'warn', catIndex, text: `${title}: ${byFlag['No variant'].length} panel(s) in no variant (${byFlag['No variant'].join(', ')})` });
    if (byFlag.Duplicate) checks.push({ level: 'warn', catIndex, text: `${title}: possible duplicates (${Array.from(new Set(byFlag.Duplicate)).join(', ')})` });
    const min = parseInt(cat.min_qty) || 1;
    const max = parseInt(cat.max_qty) || 1;
    if (max < min) checks.push({ level: 'bad', catIndex, text: `${title}: max qty ${max} is below min qty ${min}` });
    else if (!isPanel && max > 10) checks.push({ level: 'warn', catIndex, text: `${title}: max qty ${max} looks high` });
    if (stockById && cat.skus.length) {
      const live = cat.skus.filter((s) => skuStatus(stockById[s.product_id], min) === 'live').length;
      if (cat.is_mandatory && live === 0) checks.push({ level: 'bad', catIndex, text: `${title}: nothing is live at ${stockHubName}, so buyers there can't complete the kit` });
    }
  });
  if (stockById) {
    const optional = kitCategories.filter((c) => c.category_id && !c.is_mandatory);
    const liveOptional = optional.filter((c) => c.skus.some((s) => skuStatus(stockById[s.product_id], parseInt(c.min_qty) || 1) === 'live')).length;
    const need = parseInt(minOptional) || 0;
    if (need > liveOptional) {
      checks.push({ level: 'bad', catIndex: null, text: `Buyers at ${stockHubName} must pick ${need} optional categories, but only ${liveOptional} have stock there` });
    }
  }
  return checks;
};

export const KitChecks = ({ checks, stockHubName, onJump }) => {
  if (!checks.length) {
    return (
      <div style={{ padding: '10px 14px', borderRadius: '8px', background: '#d1e7dd', color: '#0f5132', fontSize: '13px', marginBottom: '16px' }}>
        No problems found{stockHubName ? ` (stock checked at ${stockHubName})` : ''}.
      </div>
    );
  }
  const bad = checks.filter((c) => c.level === 'bad').length;
  return (
    <div style={{ border: '1px solid #ffe69c', borderRadius: '8px', marginBottom: '16px', overflow: 'hidden' }}>
      <div style={{ padding: '8px 14px', background: '#fff8e1', fontSize: '13px', display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
        <strong>Checks</strong>
        {bad > 0 && <span style={{ fontSize: '11px', fontWeight: 600, background: '#f8d7da', color: '#842029', padding: '2px 8px', borderRadius: '10px' }}>{bad} serious</span>}
        <span style={{ fontSize: '11px', fontWeight: 600, background: '#fff3cd', color: '#856404', padding: '2px 8px', borderRadius: '10px' }}>{checks.length - bad} warnings</span>
        <span style={{ color: '#6c757d', fontSize: '12px' }}>Warnings only. You can still save.</span>
      </div>
      {checks.map((c, i) => (
        <div key={i} style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', padding: '7px 14px', borderTop: '1px solid #f1f3f5', fontSize: '13px', background: 'white' }}>
          <span style={{ flex: '0 0 auto', width: '18px', height: '18px', borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 700, background: c.level === 'bad' ? '#f8d7da' : '#fff3cd', color: c.level === 'bad' ? '#842029' : '#856404' }}>!</span>
          <span style={{ flex: 1, minWidth: 0 }}>{c.text}</span>
          {c.catIndex !== null && c.catIndex !== undefined && (
            <button type="button" onClick={() => onJump(c.catIndex)} style={{ flex: '0 0 auto', background: 'none', border: 'none', color: '#0d6efd', cursor: 'pointer', fontSize: '12px', padding: 0 }}>
              Go to row
            </button>
          )}
        </div>
      ))}
    </div>
  );
};

// ── Hub differences for the scope bar ────────────────────────────────────────

const HUB_FIELDS = [
  ['name', 'Kit name'],
  ['discount_percent', 'Discount %'],
  ['gst_percent', 'GST %'],
  ['min_optional_count', 'Min optional categories'],
  ['tags', 'Tags'],
];

const catKey = (c) => `${c.category_id}|${c.label || ''}`;
const catSignature = (c) => JSON.stringify([!!c.is_mandatory, c.min_qty, c.max_qty, (c.skus || []).map((s) => s.product_id).sort((a, b) => a - b)]);
const fieldValue = (kit, key) => (key === 'discount_percent' || key === 'gst_percent' ? Number(kit[key]) || 0 : (kit[key] ?? ''));
export const hubNameOf = (kit) => (kit.warehouse && kit.warehouse.name) || `Hub #${kit.warehouse_id}`;

// For the loaded copy vs its sibling copies: which hubs differ per field and per category
export const hubDifferences = (loadedKit, copies) => {
  const others = copies.filter((k) => k.id !== loadedKit.id);
  const fields = {};
  HUB_FIELDS.forEach(([key]) => {
    const hubs = others.filter((k) => String(fieldValue(k, key)) !== String(fieldValue(loadedKit, key)))
      .map((k) => `${hubNameOf(k)} (${fieldValue(k, key) || '-'})`);
    if (hubs.length) fields[key] = hubs;
  });
  const cats = {};
  (loadedKit.categories || []).forEach((c) => {
    const hubs = others.filter((k) => {
      const match = (k.categories || []).find((x) => catKey(x) === catKey(c));
      return !match || catSignature(match) !== catSignature(c);
    }).map(hubNameOf);
    if (hubs.length) cats[catKey(c)] = hubs;
  });
  const extraCats = [];
  others.forEach((k) => (k.categories || []).forEach((x) => {
    if (!(loadedKit.categories || []).some((c) => catKey(c) === catKey(x))) extraCats.push(`${hubNameOf(k)} has ${x.label || x.category_name || 'a category'}`);
  }));
  return { fields, cats, extraCats, hubCount: copies.length };
};

export const editorCatKey = (cat) => `${parseInt(cat.category_id)}|${cat.label || ''}`;

export const HubDiffNote = ({ hubs }) => {
  if (!hubs || !hubs.length) return null;
  return (
    <small style={{ display: 'block', color: '#b35c00', fontSize: '11px', marginTop: '2px' }}>
      Differs at {hubs.length > 3 ? `${hubs.slice(0, 3).join(', ')} +${hubs.length - 3} more` : hubs.join(', ')}. Changing it here overwrites theirs.
    </small>
  );
};

export const KitScopeBar = ({ scope, onChange, hubName, hubCount, diffs, loading }) => {
  const differing = diffs ? Object.keys(diffs.fields).length + Object.keys(diffs.cats).length + diffs.extraCats.length : 0;
  const options = [
    ['one', `This hub only (${hubName})`],
    ['all', `All ${hubCount} hubs with this kit`],
    ['pick', 'Pick hubs…'],
  ];
  return (
    <div style={{ border: '1px solid #b6d4fe', background: '#f3f8ff', borderRadius: '8px', padding: '12px 14px', marginBottom: '16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
        <strong style={{ fontSize: '13px', marginRight: '4px' }}>Apply changes to</strong>
        {options.map(([value, label]) => (
          <label
            key={value}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer', padding: '5px 10px', borderRadius: '6px', margin: 0, border: scope === value ? '1px solid #0d6efd' : '1px solid #dee2e6', background: scope === value ? '#e7f1ff' : 'white', fontWeight: scope === value ? 600 : 400 }}
          >
            <input type="radio" name="kit-scope" value={value} checked={scope === value} onChange={() => onChange(value)} style={{ accentColor: '#0d6efd' }} />
            {label}
          </label>
        ))}
      </div>
      <div style={{ fontSize: '12px', color: '#495057' }}>
        {scope === 'one' && `Saves to ${hubName} only. The other hubs keep their own copy.`}
        {scope === 'all' && `Saves only the fields you change, on all ${hubCount} hubs. Fields you don't touch stay as each hub has them.`}
        {scope === 'pick' && 'Saves only the fields you change, on the hubs you pick after pressing Save.'}
        {scope !== 'one' && !loading && diffs && (
          differing
            ? <span style={{ color: '#b35c00' }}> Hubs differ in {differing} place{differing === 1 ? '' : 's'}; see the notes on the fields below.</span>
            : <span style={{ color: '#0f5132' }}> All hubs currently match this copy.</span>
        )}
      </div>
      {scope !== 'one' && diffs && diffs.extraCats.length > 0 && (
        <div style={{ fontSize: '12px', color: '#b35c00' }}>Not in this copy: {Array.from(new Set(diffs.extraCats)).join('; ')}.</div>
      )}
    </div>
  );
};
